// eslint-disable-next-line no-redeclare
/* global globalThis */

import { useEffect, memo, useState } from "react";
import { styled } from "@mui/material/styles";
import { Grid, Box, Typography, Fab, Link, Zoom, TextField, Button, MenuItem, Menu, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Autocomplete, Divider } from "@mui/material";
import clsx from "clsx";
import { Send } from "@mui/icons-material";
import constructUrl from "@iamnapo/construct-url";
import { useLocation } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import queryString from "query-string";
import { Image } from "mui-image";

import cookie from "../utils/cookie.js";
import { retrieveSelfHostInfo } from "../api/index.js";
import { useSnackbar } from "../utils/index.js";
import cycloptLogo from "../assets/images/cyclopt_full.png";
import GitHub from "../assets/images/github.png";
import GitLab from "../assets/images/gitlab.png";
import BitBucket from "../assets/images/bitbucket.png";
import Azure from "../assets/images/azure.png";

const { REACT_APP_MAIN_SERVER_URL } = process.env;

const classes = {
	root: "SignIn-root",
	box: "SignIn-box",
	signIn: "SignIn-signIn",
	margin: "SignIn-margin",
	marginCyclopt: "SignIn-margin-cyclopt",
	extendedIcon: "SignIn-extendedIcon",
	boxGrid: "SignIn-boxGrid",
	noPad: "SignIn-noPad",
	subtitle: "SignIn-subtitle",
	subtitleDivider: "SignIn-subtitleDivider",
	link: "SignIn-link",
	paper: "SignIn-paper",
	logo: "SignIn-logo",
};

const StyledGrid = styled(Grid)(({ theme }) => ({
	[`&.${classes.root}`]: {
		margin: theme.spacing(0, -1),
		overflow: "hidden",
		width: "100vw",
		height: "calc(100vh - 100px)",
	},
	[`& .${classes.signIn}`]: {
		color: theme.palette.grey[700],
	},
	[`& .${classes.margin}`]: {
		margin: "10px",
		padding: "0px!important",
		borderRadius: 10,
		borderWidth: theme.spacing(0.2),
		borderStyle: "solid",
		borderColor: theme.palette.primary.main,
		backgroundColor: theme.palette.common.white,
		width: "90px",
		height: "90px",
	},
	[`& .${classes.marginCyclopt}`]: {
		margin: theme.spacing(1, 0),
		borderRadius: theme.shape.borderRadius,
		borderWidth: theme.spacing(0.3),
		borderStyle: "solid",
		borderColor: theme.palette.primary.main,
		backgroundColor: theme.palette.common.white,
		width: "270px",
		height: "45px",
	},
	[`& .${classes.extendedIcon}`]: {
		width: "100%",
	},
	[`& .${classes.boxGrid}`]: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
	},

	[`& .${classes.noPad}`]: {
		padding: "0 !important",
	},

	[`& .${classes.subtitle}`]: {
		color: theme.palette.primary.main,
		fontSize: 16,
		fontWeight: "normal",
	},

	[`& .${classes.subtitleDivider}`]: {
		color: theme.palette.secondary.main,
	},
	[`& .${classes.link}`]: {
		"&:hover": {
			color: theme.palette.primary.dark,
		},
	},
	[`& .${classes.paper}`]: {
		backgroundColor: theme.palette.secondary.main,
		borderRadius: theme.shape.borderRadius,
		boxShadow: theme.shadows[6],
		margin: theme.spacing(1, 0),
	},
	[`& .${classes.logo}`]: {
		width: "50px!important",
	},
}));

const SignIn = () => {
	const { state, search } = useLocation();
	const [gitlabAnchorEl, setGitlabAnchorEl] = useState(null);
	const [selfGitlabDialogOpen, setSelfGitlabDialogOpen] = useState(false);
	const authURL = constructUrl(REACT_APP_MAIN_SERVER_URL, "api/oauth/login", { redirectTo: globalThis.location.href.replace("/sign-in", "") });
	const [url, setUrl] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	const { error } = useSnackbar();

	useEffect(() => {
		try {
			sessionStorage.setItem("redirectTo", JSON.stringify(state?.from?.replace("/sign-in", "") || { pathname: "/" }));
		} catch { /** */ }
	}, [state]);

	useEffect(() => {
		const { selfhost } = queryString.parse(search);
		if (selfhost) {
			setSelfGitlabDialogOpen(true);
			setUrl(selfhost);
		}
	}, [search]);

	return (
		<StyledGrid container direction="row" justifyContent="center" align="center" className={classes.root}>
			<Grid item md={3} />
			<Grid item container direction="column" justifyContent="center" align="center" md={6} spacing={4} m={-2} sx={{ "> .MuiGrid-item": { p: 2 } }}>
				<Grid item container alignItems="center" justifyContent="center" mt={4}>
					<Box sx={{ minWidth: "130px", width: "40%" }}>
						<Image src={cycloptLogo} alt="Cyclopt" />
					</Box>
				</Grid>
				<Grid item container direction="row" justifyContent="center" align="center" className={classes.boxGrid} width="100%">
					<Grid item width="100%">
						<Grid container direction="column" justifyContent="center" alignItems="center">
							<Grid container direction="row" justifyContent="space-around" maxWidth={600} width="100%">
								<Divider
									className={classes.signIn}
									sx={{
										m: 1.5,
										fontSize: (t) => t.spacing(3),
										width: "100%",
										color: (t) => t.palette.grey[700],
										"&.MuiDivider-root": {
											"&::after": {
												borderColor: (t) => t.palette.grey[700],
											},
											"&::before": {
												borderColor: (t) => t.palette.grey[700],
											},
										},
									}}
								>
									{"Sign in with:"}
								</Divider>
							</Grid>
							<Grid item align="center" width="100%">
								<Grid container direction="row" justifyContent="space-between" maxWidth={600}>
									<Grid item>
										<Zoom in unmountOnExit timeout={700}>
											<Fab
												variant="extended"
												size="large"
												aria-label="Sign in with GitHub"
												title="Sign in with GitHub"
												className={classes.margin}
												component="a"
												href={`${authURL}&type=github`}
												target="_self"
											>
												<Box sx={{ textTransform: "none" }}>
													<Image src={GitHub} alt="GitHub" className={classes.logo} />
													<Typography className={classes.subtitle}>{"GitHub"}</Typography>
												</Box>
											</Fab>
										</Zoom>
									</Grid>
									<Grid item>
										<Zoom in unmountOnExit timeout={700}>
											<Fab
												variant="extended"
												size="large"
												aria-label="Sign in with Gitlab"
												title="Sign in with Gitlab"
												className={classes.margin}
												onClick={(e) => setGitlabAnchorEl(e.currentTarget)}
											>
												<Box sx={{ textTransform: "none" }}>
													<Image src={GitLab} alt="GitLab" className={classes.logo} />
													<Typography className={classes.subtitle}>{"GitLab"}</Typography>
												</Box>
											</Fab>
										</Zoom>
										<Menu
											anchorEl={gitlabAnchorEl}
											open={Boolean(gitlabAnchorEl)}
											elevation={0}
											classes={{ paper: classes.paper }}
											anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
											transformOrigin={{ vertical: "top", horizontal: "center" }}
											onClose={() => setGitlabAnchorEl(null)}
										>
											<MenuItem onClick={() => setGitlabAnchorEl(null)}>
												<Typography
													component="a"
													href={`${authURL}&type=gitlab`}
													target="_self"
													sx={{ color: "common.black" }}
												>
													{"GitLab.com"}
												</Typography>
											</MenuItem>
											<MenuItem onClick={() => setGitlabAnchorEl(null)}>
												<Typography
													sx={{ color: "common.black" }}
													onClick={() => {
														setSelfGitlabDialogOpen(true);
													}}
												>
													{"Self-hosted instance"}
												</Typography>
											</MenuItem>
										</Menu>
										<Dialog
											keepMounted
											open={selfGitlabDialogOpen}
											TransitionComponent={Zoom}
											onClose={() => {
												if (!isSubmitting) {
													setUrl("");
													setSelfGitlabDialogOpen(false);
												}
											}}
										>
											<form onSubmit={async (e) => {
												e.preventDefault();
												setIsSubmitting(true);
												try {
													const info = await retrieveSelfHostInfo(url);
													cookie.set("_cyclopt_selfhosted", info);
													setIsSubmitting(false);
													setSelfGitlabDialogOpen(false);
													localStorage.setItem(
														"recent-selfhosted-urls",
														JSON.stringify([...new Set([
															...(localStorage.getItem("recent-selfhosted-urls") || []),
															url,
														])]),
													);
													globalThis.location = `${authURL}&type=${info.type}`;
												} catch (error_) {
													setIsSubmitting(false);
													const { message } = await error_.response.json();
													error(message);
												}
											}}
											>
												<DialogTitle>
													{"Self-hosted GitLab Instance"}
												</DialogTitle>
												<DialogContent dividers>
													<DialogContentText>
														{"If you have a self-hosted GitLab instance that you know we support, enter it’s URL below:"}
													</DialogContentText>
													<Autocomplete
														selectOnFocus
														clearOnBlur
														freeSolo
														handleHomeEndKeys
														value={url}
														size="small"
														renderInput={(params) => (
															<TextField
																{...params}
																autoFocus
																fullWidth
																required
																margin="dense"
																id="url"
																label="Self-Hosted GitLab URL"
																placeholder="https://gitlab.example.com"
																type="url"
																value={url}
																onChange={((e) => setUrl(e.target.value))}
															/>
														)}
														options={
															(() => {
																try {
																	return JSON.parse(localStorage.getItem("recent-selfhosted-urls")) || [];
																} catch {
																	return [];
																}
															})()
														}
														onChange={(_, val) => setUrl(val || "")}
													/>
												</DialogContent>
												<DialogActions>
													<Button
														variant="outlined"
														type="reset"
														disabled={isSubmitting}
														onClick={() => {
															setSelfGitlabDialogOpen(false);
															setUrl("");
														}}
													>
														{"Cancel"}
													</Button>
													<LoadingButton
														autoFocus
														type="submit"
														disabled={!url}
														endIcon={<Send />}
														loading={isSubmitting}
														loadingPosition="end"
														variant="contained"
													>
														{"Submit"}
													</LoadingButton>
												</DialogActions>
											</form>
										</Dialog>
									</Grid>
									<Grid item>
										<Zoom in unmountOnExit timeout={700}>
											<Fab
												variant="extended"
												size="large"
												aria-label="Sign in with Azure"
												title="Sign in with Azure"
												className={classes.margin}
												component="a"
												href={`${authURL}&type=azure`}
												target="_self"
											>
												<Box sx={{ textTransform: "none" }}>
													<Image src={Azure} alt="Azure" className={classes.logo} />
													<Typography className={classes.subtitle}>{"Azure"}</Typography>
												</Box>
											</Fab>
										</Zoom>
									</Grid>
									<Grid item>
										<Zoom in unmountOnExit timeout={700}>
											<Fab
												variant="extended"
												size="large"
												aria-label="Sign in with BitBucket"
												title="Sign in with BitBucket"
												className={classes.margin}
												component="a"
												href={`${authURL}&type=bitbucket`}
												target="_self"
											>
												<Box sx={{ textTransform: "none" }}>
													<Image src={BitBucket} alt="BitBucket" className={classes.logo} />
													<Typography className={classes.subtitle}>{"Bitbucket"}</Typography>
												</Box>
											</Fab>
										</Zoom>
									</Grid>
								</Grid>
								<Grid container direction="row" justifyContent="space-around" maxWidth={600}>
									<Divider
										className={classes.signIn}
										sx={{
											m: 1.5,
											fontSize: (t) => t.spacing(2),
											width: "100%",
											color: (t) => t.palette.grey[700],
											"&.MuiDivider-root": {
												"&::after": {
													borderColor: (t) => t.palette.grey[700],
												},
												"&::before": {
													borderColor: (t) => t.palette.grey[700],
												},
											},
										}}
									>
										{"OR"}
									</Divider>
								</Grid>
								<Grid item>
									<Zoom in unmountOnExit timeout={700}>
										<Fab
											variant="extended"
											aria-label="Sign in with Cyclopt"
											title="Sign in with Cyclopt"
											className={classes.margin}
											component="a"
											href={`${authURL}&type=cyclopt`}
											target="_self"
											sx={{ width: "270px !important", height: "45px !important" }}
										>
											<Grid container direction="row" justifyContent="center" sx={{ alignItems: "center", textTransform: "none" }}>
												<Typography variant="h6" fontWeight="normal" className={classes.signIn} mr={1}>{"Sign in with:"}</Typography>
												<Image src={cycloptLogo} alt="Cyclopt" width="45%" />
											</Grid>
										</Fab>
									</Zoom>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
				<Grid item container direction="row" justifyContent="center" spacing={1} m={-0.5} sx={{ "> .MuiGrid-item": { p: 0.5 } }}>
					<Grid item>
						<Link
							variant="h6"
							className={clsx(classes.subtitle, classes.link)}
							href="https://cyclopt.com"
							target="_blank"
							rel="noopener noreferrer"
							underline="none"
						>
							{"About"}
						</Link>
					</Grid>
					<Grid item><Typography className={classes.subtitleDivider}>{"|"}</Typography></Grid>
					<Grid item>
						<Link
							variant="h6"
							href="https://cyclopt.com/blog"
							target="_blank"
							rel="noopener noreferrer"
							className={clsx(classes.subtitle, classes.link)}
							underline="none"
						>
							{"Blog"}
						</Link>
					</Grid>
					<Grid item><Typography className={classes.subtitleDivider}>{"|"}</Typography></Grid>
					<Grid item>
						<Link
							variant="h6"
							href="https://cyclopt.com/tos"
							target="_blank"
							rel="noopener noreferrer"
							className={clsx(classes.subtitle, classes.link)}
							underline="none"
						>
							{"ToS"}
						</Link>
					</Grid>
					<Grid item><Typography className={classes.subtitleDivider}>{"|"}</Typography></Grid>
					<Grid item>
						<Link
							variant="h6"
							href="https://cyclopt.com/privacy"
							target="_blank"
							rel="noopener noreferrer"
							className={clsx(classes.subtitle, classes.link)}
							underline="none"
						>
							{"Privacy"}
						</Link>
					</Grid>
				</Grid>
			</Grid>
			<Grid item md={3} />
		</StyledGrid>
	);
};

export default memo(SignIn);
